export const DEFAULT_FONT_FAMILY = 'Roboto, sans-serif';
export const DEFAULT_FONT_SIZE = '11px';

export const THEME_FONT_STORAGE_KEY = 'medskyFontFamily';
export const THEME_FONT_CHANGED_EVENT = 'medsky-theme-font-changed';

export const THEME_FONT_SIZE_STORAGE_KEY = 'medskyFontSize';
export const THEME_FONT_SIZE_CHANGED_EVENT = 'medsky-theme-font-size-changed';

export const CSS_FONT_FAMILY = 'var(--app-font-family)';
export const CSS_FONT_SIZE = 'var(--app-font-size)';

/** @deprecated Use CSS_FONT_FAMILY or getThemeFontFamily() */
export const FONT_FAMILY = DEFAULT_FONT_FAMILY;
export const FONTSIZE = CSS_FONT_SIZE;

export const FONT_FAMILY_PRESETS = [
  { label: 'Roboto', value: 'Roboto, sans-serif', group: 'Google' },
  { label: 'Poppins', value: 'Poppins, sans-serif', group: 'Google' },
  { label: 'Open Sans', value: '"Open Sans", sans-serif', group: 'Google' },
  { label: 'Montserrat', value: 'Montserrat, sans-serif', group: 'Google' },
  { label: 'Nunito', value: 'Nunito, sans-serif', group: 'Google' },
  { label: 'Segoe UI', value: '"Segoe UI", Tahoma, sans-serif', group: 'System' },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif', group: 'System' },
  { label: 'Georgia', value: 'Georgia, serif', group: 'System' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif', group: 'System' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace', group: 'System' },
  { label: 'System UI', value: 'system-ui, -apple-system, sans-serif', group: 'System' }
];

export const normalizeFontFamily = (input) => {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  return trimmed.length > 0 ? trimmed : null;
};
export const DEFAULT_PRIMARY_COLOR = '#089bab';
export const DEFAULT_PRIMARY_COLOR_HOVER = '#067d8b';

/** @deprecated Use CSS var(--primary) or getThemeColors() for dynamic theme */
export const PRIMARY_COLOR = DEFAULT_PRIMARY_COLOR;
/** @deprecated Use CSS var(--primary-hover) or getThemeColors() */
export const PRIMARY_COLOR_HOVER = DEFAULT_PRIMARY_COLOR_HOVER;

export const THEME_COLORS_STORAGE_KEY = 'medskyBrandColors';
export const THEME_COLORS_CHANGED_EVENT = 'medsky-theme-colors-changed';

export const CSS_PRIMARY = 'var(--primary)';
export const CSS_PRIMARY_HOVER = 'var(--primary-hover)';

/** Parse #RGB, #RRGGBB, or #RRGGBBAA into { r, g, b }. */
const hexToRgb = (hex) => {
  let h = hex.replace('#', '').trim();
  if (h.length === 8) h = h.slice(0, 6);
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

export const normalizeHexColor = (color) => {
  if (!color || typeof color !== 'string') return null;
  let c = color.trim();
  if (!c.startsWith('#')) c = `#${c}`;
  if (c.length === 4) {
    c = `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
  }
  if (c.length === 7 || c.length === 9) return c;
  return null;
};

export const getThemeColors = () => {
  try {
    const raw = localStorage.getItem(THEME_COLORS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const primary = normalizeHexColor(parsed.primary);
      const primaryHover = normalizeHexColor(parsed.primaryHover);
      if (primary && primaryHover) {
        return { primary, primaryHover };
      }
    }
  } catch {
    /* use defaults */
  }
  return {
    primary: DEFAULT_PRIMARY_COLOR,
    primaryHover: DEFAULT_PRIMARY_COLOR_HOVER
  };
};

/** Apply brand colors to CSS custom properties on :root. */
export const applyThemeCssVariables = (colors = getThemeColors()) => {
  const primary = colors.primary || DEFAULT_PRIMARY_COLOR;
  const primaryHover = colors.primaryHover || DEFAULT_PRIMARY_COLOR_HOVER;
  const root = document.documentElement;
  const { r, g, b } = hexToRgb(primary);

  root.style.setProperty('--primary', primary);
  root.style.setProperty('--primary-hover', primaryHover);
  root.style.setProperty('--primary-dark', primaryHover);
  root.style.setProperty('--bs-primary', primary);
  root.style.setProperty('--bs-primary-rgb', `${r}, ${g}, ${b}`);

  for (let i = 1; i <= 9; i += 1) {
    root.style.setProperty(`--rgba-primary-${i}`, `rgba(${r}, ${g}, ${b}, ${i * 0.1})`);
  }
};

/** Persist and apply brand colors; notifies listeners via custom event. */
export const setThemeColors = (primary, primaryHover) => {
  const colors = {
    primary: normalizeHexColor(primary) || DEFAULT_PRIMARY_COLOR,
    primaryHover: normalizeHexColor(primaryHover) || DEFAULT_PRIMARY_COLOR_HOVER
  };
  localStorage.setItem(THEME_COLORS_STORAGE_KEY, JSON.stringify(colors));
  applyThemeCssVariables(colors);
  window.dispatchEvent(new CustomEvent(THEME_COLORS_CHANGED_EVENT, { detail: colors }));
  return colors;
};

/** Load saved brand colors on app startup. */
export const initThemeColors = () => applyThemeCssVariables(getThemeColors());

export const getThemeFontFamily = () => {
  try {
    const saved = localStorage.getItem(THEME_FONT_STORAGE_KEY);
    if (saved) {
      const preset = FONT_FAMILY_PRESETS.find((f) => f.value === saved);
      if (preset) return saved;
      if (saved.trim().length > 0) return saved;
    }
  } catch {
    /* use default */
  }
  return DEFAULT_FONT_FAMILY;
};

/** Apply font family to CSS custom properties and document body. */
export const applyThemeFontFamily = (fontFamily = getThemeFontFamily()) => {
  const family = fontFamily || DEFAULT_FONT_FAMILY;
  const root = document.documentElement;

  root.style.setProperty('--app-font-family', family);
  root.style.setProperty('--bs-body-font-family', family);
  document.body.style.fontFamily = family;
};

/** Persist and apply font family; notifies listeners via custom event. */
export const setThemeFontFamily = (fontFamily) => {
  const family = normalizeFontFamily(fontFamily) || DEFAULT_FONT_FAMILY;
  localStorage.setItem(THEME_FONT_STORAGE_KEY, family);
  applyThemeFontFamily(family);
  window.dispatchEvent(
    new CustomEvent(THEME_FONT_CHANGED_EVENT, { detail: { fontFamily: family } })
  );
  return family;
};

export const getThemeFontSize = () => {
  try {
    const saved = localStorage.getItem(THEME_FONT_SIZE_STORAGE_KEY);
    if (saved && saved.trim().length > 0) return saved;
  } catch {
    /* use default */
  }
  return DEFAULT_FONT_SIZE;
};

export const applyThemeFontSize = (fontSize = getThemeFontSize()) => {
  const size = fontSize || DEFAULT_FONT_SIZE;
  const root = document.documentElement;
  root.style.setProperty('--app-font-size', size);
};

export const setThemeFontSize = (fontSize) => {
  let size = fontSize;
  if (!size) size = DEFAULT_FONT_SIZE;
  if (!isNaN(size) && size.toString().trim() !== '') {
    size = `${size}px`;
  }
  localStorage.setItem(THEME_FONT_SIZE_STORAGE_KEY, size);
  applyThemeFontSize(size);
  window.dispatchEvent(
    new CustomEvent(THEME_FONT_SIZE_CHANGED_EVENT, { detail: { fontSize: size } })
  );
  return size;
};

/** Load saved colors and font on app startup. */
export const initTheme = () => {
  initThemeColors();
  applyThemeFontFamily();
  applyThemeFontSize();
};

export const FilterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1,
    backgroundColor: '#fcfdff',
    height: '30px',
    fontSize: FONTSIZE,
    fontFamily: CSS_FONT_FAMILY,
    '& input': {
      fontSize: FONTSIZE,
      padding: '0 10px',
      height: '100%',
      boxSizing: 'border-box',
      lineHeight: '30px'
    },
    '& .MuiSelect-select': {
      fontSize: FONTSIZE,
      padding: '0 10px !important',
      height: '100% !important',
      display: 'flex !important',
      alignItems: 'center !important',
      boxSizing: 'border-box',
      minHeight: '30px !important'
    },
    '& fieldset': {
      borderColor: CSS_PRIMARY
    },
    '&:hover fieldset': {
      borderColor: CSS_PRIMARY_HOVER
    },
    '&.Mui-focused fieldset': {
      borderColor: CSS_PRIMARY
    }
  },
  '& .MuiInputLabel-root': {
    color: CSS_PRIMARY,
    fontSize: FONTSIZE,
    transform: 'translate(8px, 8px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(8px, -7px) scale(0.8)'
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: CSS_PRIMARY
  },
  '& .MuiInputBase-input::placeholder': {
    color: CSS_PRIMARY,
    opacity: 1
  },
  '& .MuiSvgIcon-root': {
    color: CSS_PRIMARY
  }
};

export const TextFeaildSx = {
  '& .MuiInputBase-input': {
    fontSize: FONTSIZE,
    fontFamily: CSS_FONT_FAMILY
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: CSS_PRIMARY
    },
    '&:hover fieldset': {
      borderColor: CSS_PRIMARY_HOVER
    },
    '&.Mui-focused fieldset': {
      borderColor: CSS_PRIMARY
    }
  },
  '& .MuiInputLabel-root': {
    color: CSS_PRIMARY,
    fontSize: FONTSIZE,
    transform: 'translate(8px, 8px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(8px, -7px) scale(0.8)'
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: CSS_PRIMARY
  },
  '& .MuiInputBase-input::placeholder': {
    color: CSS_PRIMARY,
    opacity: 1
  },
  '& .MuiSvgIcon-root': {
    color: CSS_PRIMARY
  }
};

export const DropDownSX = {
  width: '100%',
  minWidth: '300px',
  fontSize: FONTSIZE,
  '& .MuiAutocomplete-option': {
    fontSize: FONTSIZE,
    fontFamily: CSS_FONT_FAMILY
  },
  '& .MuiCheckbox-root': {
    color: CSS_PRIMARY
  },
  '& .MuiCheckbox-root.Mui-checked': {
    color: CSS_PRIMARY
  }
};

export const DropDownSXWithColor = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: CSS_PRIMARY
    },
    '&:hover fieldset': {
      borderColor: CSS_PRIMARY_HOVER
    },
    '&.Mui-focused fieldset': {
      borderColor: CSS_PRIMARY
    }
  },
  '& .MuiInputLabel-root': {
    color: CSS_PRIMARY
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: CSS_PRIMARY
  },
  '& .MuiSvgIcon-root': {
    color: CSS_PRIMARY
  }
};

export const DatePickerSX = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.85rem',
    color: CSS_PRIMARY,
    '& fieldset': {
      borderColor: CSS_PRIMARY
    },
    '&:hover fieldset': {
      borderColor: CSS_PRIMARY_HOVER
    },
    '&.Mui-focused fieldset': {
      borderColor: CSS_PRIMARY
    }
  },
  '& .MuiInputLabel-root': {
    color: CSS_PRIMARY,
    fontSize: '0.85rem'
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: CSS_PRIMARY
  },
  '& .MuiInputBase-input': {
    fontSize: '0.85rem'
  },
  '& .MuiSvgIcon-root': {
    color: CSS_PRIMARY
  }
};
